import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";

import { useAppDispatch, useAppSelector } from "store";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import { CollectionItem } from "store/gameCenter/collections/slice";
import { TagItem } from "store/gameCenter/tags/slice";

import { usePermissions } from "helpers/usePermission";

import RoundBtn from "components/buttons/RoundBtn";

import MainPageSortItem from "./components/MainPageSortItem";
import MainPageDragItem from "./components/MainPageDragItem";

import "./Widgets.scss";
import { selectFilteredGeo } from "store/geoFilter/selectors";
import WidgetsListSkeleton from "components/global/Skeleton/WidgetsListSkeleton/WidgetsListSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { getCategoriesList } from "store/gameCenter/categories/slice";

const ITEM_TYPE = {
  COLLECTION: "collection",
  TAG: "tag",
  PAGE_ITEM: "page_item",
  CATEGORY: "category",
  MULTI_SCOPE: "multiScope",
};

interface IPageItem {
  instanceType: "collection" | "tag" | "category" | "multiScope";
  instanceId: number;
  title: string;
  image?: string | null;
  icon?: string | null;
}

interface SelectableItem {
  value: number;
  label: string;
  img: string;
  type: "collection" | "tag" | "category" | "multiScope";
}

interface PageItem {
  value: number;
  label: string;
  type: "collection" | "tag" | "category" | "multiScope";
  img: string;
}

const GameBar: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const activePermissions = usePermissions("cms_widgets_game_bar");
  const geoList = useAppSelector(selectFilteredGeo);
  const [activeGeo, setActiveGeo] = useState<string>("");
  const [collections, setCollections] = useState<SelectableItem[]>([]);
  const [tags, setTags] = useState<SelectableItem[]>([]);
  const [multiScopes, setMultiScopes] = useState<SelectableItem[]>([]);
  const [pageItems, setPageItems] = useState<PageItem[]>([]);
  const [pageItemsTags, setPageItemsTags] = useState<number[]>([]);
  const [pageItemsCollection, setPageItemsCollection] = useState<number[]>([]);
  const [pageItemsCategories, setPageItemsCategories] = useState<number[]>([]);
  const [pageItemsMultiScopes, setPageItemsMultiScopes] = useState<number[]>(
    [],
  );
  const [selectedItems, setSelectedItems] = useState<SelectableItem[]>([]);
  const [selectedItemsToDelete, setSelectedItemsToDelete] = useState<
    SelectableItem[]
  >([]);
  const [itemsFetched, setItemsFetched] = useState(false);
  const categories = useAppSelector((store) => {
    const data = store.categories.list.map((s) => ({
      value: s.id,
      label: s.title,
      img: s.image,
      type: ITEM_TYPE.CATEGORY,
    }));

    // value: s.value,
    // label: s.label,
    // img: s.img,
    // type: s.type,

    return data;
  });

  const permissions = usePermissionHelper();

  useEffect(() => {
    if (!activePermissions?.includes("read") && activePermissions !== null) {
      navigate("/dashboard");
    }
  }, []);

  useEffect(() => {
    if (geoList?.length) {
      setActiveGeo(geoList[0]);
    }
  }, [geoList]);

  const getPageItems = async (availableMultiScopes = multiScopes) => {
    try {
      const result = await instance.get(
        `${apiRoutes.gameBarList}?geo=${activeGeo}`,
      );
      setPageItems(
        result.data.items.map((el: IPageItem) => {
          const multiScope = availableMultiScopes.find(
            (item) =>
              el.instanceType === ITEM_TYPE.MULTI_SCOPE &&
              item.value === el.instanceId,
          );

          return {
            value: el.instanceId,
            type: el.instanceType,
            label: el.title || multiScope?.label || t(el.instanceType),
            img: el.image || el.icon || multiScope?.img || "",
            showType: true,
          };
        }),
      );

      setPageItemsCollection(
        result.data.items
          .filter((el: IPageItem) => el.instanceType === "collection")
          .map((item: IPageItem) => item.instanceId),
      );

      setPageItemsTags(
        result.data.items
          .filter((el: IPageItem) => el.instanceType === "tag")
          .map((item: IPageItem) => item.instanceId),
      );

      setPageItemsCategories(
        result.data.items
          .filter((el: IPageItem) => el.instanceType === "category")
          .map((item: IPageItem) => item.instanceId),
      );

      setPageItemsMultiScopes(
        result.data.items
          .filter((el: IPageItem) => el.instanceType === ITEM_TYPE.MULTI_SCOPE)
          .map((item: IPageItem) => item.instanceId),
      );
    } catch (err) {
      console.error(err);
    }

    setItemsFetched(true);
  };

  useEffect(() => {
    if (!activeGeo) {
      return;
    }

    setItemsFetched(false);
    dispatch(
      getCategoriesList({
        isActive: 1,
      }),
    );

    const loadItems = async () => {
      const availableMultiScopes = await getMultiScopes();
      getPageItems(availableMultiScopes);
    };

    loadItems();
    getTags();
    getCollections();
  }, [activeGeo]);

  const getCollections = async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.collectionsList}?limit=500&offset=0`,
      );

      setCollections(
        result.data.items.map((el: CollectionItem) => ({
          value: el.id,
          label: el.title,
          img: el.image,
          type: ITEM_TYPE.COLLECTION,
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const getTags = async () => {
    try {
      const res = await instance.get(
        `${apiRoutes.tagsList}?limit=500&offset=0`,
      );
      setTags(
        res.data.items.map((el: TagItem) => ({
          value: el.id,
          label: el.title,
          img: el.image,
          type: ITEM_TYPE.TAG,
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const getMultiScopes = async (): Promise<SelectableItem[]> => {
    try {
      const result = await instance.get(apiRoutes.multiScopeList, {
        params: { limit: 500, offset: 0 },
      });
      const data = result.data?.data ?? result.data;
      const items =
        data.items?.map(
          (el: {
            id: number;
            title: string;
            image?: string | null;
            icon?: string | null;
          }) => ({
            value: el.id,
            label: el.title,
            img: el.image || el.icon || "",
            type: ITEM_TYPE.MULTI_SCOPE,
          }),
        ) ?? [];
      setMultiScopes(items);
      return items;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const handleUpdateItems = async (items: any[]) => {
    if (!activeGeo) return;

    try {
      await instance.post(`${apiRoutes.gameBarUpdate}`, {
        geo: activeGeo,
        items: items.map((el, i) => {
          return {
            instanceType: el.type,
            instanceId: el.value,
            priorityOrder: i + 1,
          };
        }),
      });

      setPageItems([]);
      setPageItemsCollection([]);
      setPageItemsTags([]);
      setPageItemsCategories([]);
      setPageItemsMultiScopes([]);
      setItemsFetched(false);
      getPageItems();
      dispatch(
        showNotification({
          title: t("success"),
          message: t("itemsUpdated"),
          type: NotificationTypes.success,
        }),
      );
    } catch (err) {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("itemsUpdateError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const moveItem = async (dragIndex: number, hoverIndex: number) => {
    const newItems = [...pageItems];
    const [moved] = newItems.splice(dragIndex, 1);
    newItems.splice(hoverIndex, 0, moved);
    setPageItems(newItems);
    await handleUpdateItems(newItems);
  };

  const isSelected = (item: SelectableItem) => {
    return selectedItems.some(
      (selected) =>
        selected.value === item.value && selected.type === item.type,
    );
  };

  return (
    <div className="main-page-wrapper game-bar">
      <div className="main-page-content">
        <div className="main-page-widget">
          <div className="main-page-widget--box">
            <div className="main-page-widget--tabs">
              <div className="game-modal-tabs small">
                {geoList?.map((el) => (
                  <div
                    className={`tab-item ${activeGeo === el ? "active" : ""}`}
                    key={el}
                    onClick={() => {
                      setActiveGeo(el);
                      setPageItems([]);
                      setPageItemsCollection([]);
                      setPageItemsTags([]);
                      setPageItemsCategories([]);
                      setPageItemsMultiScopes([]);
                    }}
                  >
                    {el.toLowerCase() === "ww" ? (
                      <span className="mr-5">🌎</span>
                    ) : (
                      <img
                        src={`https://flagcdn.com/w40/${el.toLowerCase()}.png`}
                        alt=""
                      />
                    )}
                    {t(el)}
                  </div>
                ))}
              </div>
            </div>
            {itemsFetched ? (
              <div className="main-page-widget--list">
                {pageItems.map((el, index) => (
                  <MainPageSortItem
                    isMainWidgetPage
                    handleClick={() => {
                      if (selectedItemsToDelete.includes(el)) {
                        setSelectedItemsToDelete(
                          selectedItemsToDelete.filter((item) => item !== el),
                        );
                      } else {
                        setSelectedItemsToDelete([
                          ...selectedItemsToDelete,
                          el,
                        ]);
                      }
                    }}
                    isSelected={selectedItemsToDelete.includes(el)}
                    key={`${el.value}-${el.type}`}
                    item={el}
                    index={index}
                    moveItem={moveItem}
                    isDraggable={permissions?.update}
                    readOnly={!permissions?.update}
                  />
                ))}
              </div>
            ) : (
              <WidgetsListSkeleton />
            )}
            <div className="main-page-btns">
              {permissions?.create && (
                <RoundBtn
                  handleClick={() => {
                    setItemsFetched(false);

                    const mappedNewItems = [
                      ...pageItems,
                      ...selectedItems.map((s) => ({
                        value: s.value,
                        label: s.label,
                        img: s.img,
                        type: s.type,
                      })),
                    ];

                    setSelectedItems([]);
                    setPageItems(mappedNewItems);
                    handleUpdateItems(mappedNewItems);
                  }}
                  txt="+"
                  disabled={selectedItems.length === 0}
                />
              )}
              {permissions?.delete && (
                <RoundBtn
                  handleClick={() => {
                    setItemsFetched(false);
                    const newArray = pageItems.filter(
                      (el) => !selectedItemsToDelete.includes(el),
                    );
                    handleUpdateItems(newArray);
                    setSelectedItemsToDelete([]);
                    setPageItems(newArray);
                  }}
                  txt="-"
                  disabled={selectedItemsToDelete.length === 0}
                />
              )}
            </div>
          </div>
        </div>
        <div className="main-page-lists">
          <div className="game-page-list-column">
            <div className="game-page-list-box collections-list-box">
              <div className="main-page-title collections ">
                {t("collections")}
              </div>
              <div className="game-page-list-content">
                {itemsFetched ? (
                  <>
                    {collections
                      .filter(
                        (item) => !pageItemsCollection.includes(item.value),
                      )
                      .map((el: any) => (
                        <MainPageDragItem
                          handleClick={() => {
                            if (isSelected(el)) {
                              setSelectedItems((s) =>
                                s.filter(
                                  (s) =>
                                    !(
                                      s.value === el.value && s.type === el.type
                                    ),
                                ),
                              );
                            }

                            if (!isSelected(el)) {
                              setSelectedItems((s) => [...s, el]);
                            }
                          }}
                          isSelected={isSelected(el)}
                          key={el.value}
                          item={el}
                          type={ITEM_TYPE.COLLECTION}
                        />
                      ))}
                  </>
                ) : (
                  <WidgetsListSkeleton />
                )}
              </div>
            </div>
            <div className="game-page-list-box multi-scope-list-box">
              <div className="main-page-title multi-scope">
                {t("multiScope")}
              </div>
              <div className="game-page-list-content">
                {itemsFetched ? (
                  multiScopes
                    .filter(
                      (item) => !pageItemsMultiScopes.includes(item.value),
                    )
                    .map((el) => (
                      <MainPageDragItem
                        handleClick={() => {
                          if (isSelected(el)) {
                            setSelectedItems((items) =>
                              items.filter(
                                (item) =>
                                  item.value !== el.value ||
                                  item.type !== el.type,
                              ),
                            );
                          } else {
                            setSelectedItems((items) => [...items, el]);
                          }
                        }}
                        isSelected={isSelected(el)}
                        key={el.value}
                        item={el}
                        type={ITEM_TYPE.MULTI_SCOPE}
                      />
                    ))
                ) : (
                  <WidgetsListSkeleton />
                )}
              </div>
            </div>
          </div>
          <div className="game-page-list-box">
            <div className="main-page-title tags">{t("tags")}</div>
            <div className="game-page-list-content">
              {itemsFetched ? (
                <>
                  {tags
                    .filter((item) => !pageItemsTags.includes(item.value))
                    .map((el: any) => (
                      <MainPageDragItem
                        handleClick={() => {
                          if (isSelected(el)) {
                            setSelectedItems((s) =>
                              s.filter(
                                (s) =>
                                  !(s.value === el.value && s.type === el.type),
                              ),
                            );
                          }

                          if (!isSelected(el)) {
                            setSelectedItems((s) => [...s, el]);
                          }
                        }}
                        isSelected={isSelected(el)}
                        key={el.value}
                        item={el}
                        type={ITEM_TYPE.TAG}
                      />
                    ))}
                </>
              ) : (
                <WidgetsListSkeleton />
              )}
            </div>
          </div>
          <div className="game-page-list-box">
            <div className="main-page-title categories">{t("categories")}</div>
            <div className="game-page-list-content">
              {itemsFetched ? (
                <>
                  {categories
                    .filter((item) => !pageItemsCategories.includes(item.value))
                    .map((el: any) => (
                      <MainPageDragItem
                        handleClick={() => {
                          if (isSelected(el)) {
                            setSelectedItems((s) =>
                              s.filter(
                                (s) =>
                                  !(s.value === el.value && s.type === el.type),
                              ),
                            );
                          }

                          if (!isSelected(el)) {
                            setSelectedItems((s) => [...s, el]);
                          }
                        }}
                        isSelected={isSelected(el)}
                        key={el.value}
                        item={el}
                        type={ITEM_TYPE.CATEGORY}
                      />
                    ))}
                </>
              ) : (
                <WidgetsListSkeleton />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameBar;
